import React, { useEffect, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from "@material-ui/core";
import {
  IRemyndVerifiedUserProductWarrantySaveReq,
  IUserProductBrief
} from "../types";
import { ShowValue } from "src/apps/commons/dataDisplays";
import { ImageAvatar, ImageUpload } from "src/apps/commons/fileUploads";
import { yupTrimToNull } from "src/utils/stringUtils";
import { warrantyTypes } from "src/utils/enum";
import { calcEndDateFromDuration } from "../utils";
import { formatDate } from "../../../../utils";

interface Props {
  label: string;
  userProduct: IUserProductBrief;
  open: boolean;
  defaultValue?: IRemyndVerifiedUserProductWarrantySaveReq;
  handleCloseModal: () => void;
  handleSubmitModal: (value: IRemyndVerifiedUserProductWarrantySaveReq) => void;
}

type FormData = {
  active: boolean;
  startDate?: LocalDate;
  endDate?: LocalDate;

  labelTh?: string;
  labelEn?: string;
  type: WarrantyType;
  years?: Int;
  months?: Int;
  days?: Int;
  remarkTh?: string;
  remarkEn?: string;
  fileId?: FileUploadId;
};

const schema = yup.object().shape({
  labelTh: yup.string().transform(yupTrimToNull),
  labelEn: yup.string().transform(yupTrimToNull),
  type: yup.string().required("กรุณาระบุ ประเภทการรับประกัน"),
  years: yup
    .number()
    .default(0)
    .min(0, "ต้องมากกว่าหรือเท่ากับ 0")
    .integer("ต้องเป็นจำนวนเต็ม"),
  months: yup
    .number()
    .default(0)
    .min(0, "ต้องมากกว่าหรือเท่ากับ 0")
    .integer("ต้องเป็นจำนวนเต็ม"),
  days: yup
    .number()
    .default(0)
    .min(0, "ต้องมากกว่าหรือเท่ากับ 0")
    .integer("ต้องเป็นจำนวนเต็ม"),
  remarkTh: yup.string().transform(yupTrimToNull),
  remarkEn: yup.string().transform(yupTrimToNull),
  fileId: yup.string().transform(yupTrimToNull),
  startDate: yup.string().transform(yupTrimToNull),
  endDate: yup.string().transform(yupTrimToNull),
  active: yup.boolean()
});

const UserProductWarrantyModal: React.FC<Props> = props => {
  const {
    open,
    label,
    userProduct,
    defaultValue,
    handleCloseModal,
    handleSubmitModal
  } = props;

  const defaultFormData = useMemo<FormData>(
    () => ({
      active: defaultValue?.active ?? false,
      startDate: defaultValue?.startDate ?? "",
      endDate: defaultValue?.endDate ?? "",
      labelTh: defaultValue?.labelTh ?? "",
      labelEn: defaultValue?.labelEn ?? "",
      type: defaultValue?.type ?? "DURATION",
      years: defaultValue?.years ?? 0,
      months: defaultValue?.months ?? 0,
      days: defaultValue?.days ?? 0,
      remarkTh: defaultValue?.remarkTh ?? "",
      remarkEn: defaultValue?.remarkEn ?? "",
      fileId: defaultValue?.fileId ?? ""
    }),
    [defaultValue]
  );

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
    setValue
  } = useForm<FormData>({
    defaultValues: defaultFormData,
    resolver: yupResolver(schema)
  });

  const handleTransformAndSubmit = (form: FormData) => {
    const isDuration = form.type === "DURATION";
    handleSubmitModal({
      ...defaultValue,
      ord: 0, // will be set at final submit
      active: form.active,
      type: form.type,
      labelTh: form.labelTh,
      labelEn: form.labelEn,
      remarkTh: form.remarkTh,
      remarkEn: form.remarkEn,
      fileId: form.fileId,
      startDate: isDuration ? form.startDate : undefined,
      endDate: isDuration ? form.endDate : undefined,
      years: isDuration ? form.years : undefined,
      months: isDuration ? form.months : undefined,
      days: isDuration ? form.days : undefined
    });
  };

  const type = watch("type");
  const startDate = watch("startDate");
  const years = watch("years");
  const months = watch("months");
  const days = watch("days");

  useEffect(() => {
    setValue(
      "endDate",
      calcEndDateFromDuration(
        startDate,
        Number(years),
        Number(months),
        Number(days)
      ) ?? ""
    );
  }, [setValue, startDate, years, months, days]);

  return (
    <Dialog
      open={open}
      onClose={handleCloseModal}
      maxWidth="md"
      fullWidth
      scroll="body"
      aria-labelledby="form-dialog-title"
    >
      <form onSubmit={handleSubmit(handleTransformAndSubmit)}>
        <DialogTitle id="form-dialog-title" disableTypography>
          <Typography variant="h3">
            {!defaultValue ? "เพิ่ม " : "แก้ไข "}
            {label}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Controller
                name="labelTh"
                control={control}
                defaultValue={defaultFormData.labelEn ?? ""}
                render={({ field }) => (
                  <TextField
                    variant="outlined"
                    fullWidth
                    {...field}
                    label="ชื่อ (TH)"
                    error={Boolean(errors.labelEn)}
                    helperText={errors.labelEn?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="labelEn"
                control={control}
                defaultValue={defaultFormData.labelEn ?? ""}
                render={({ field }) => (
                  <TextField
                    variant="outlined"
                    fullWidth
                    {...field}
                    label="ชื่อ (EN)"
                    error={Boolean(errors.labelEn)}
                    helperText={errors.labelEn?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider light />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="remarkTh"
                control={control}
                defaultValue={defaultFormData.remarkTh ?? ""}
                render={({ field }) => (
                  <TextField
                    variant="outlined"
                    fullWidth
                    {...field}
                    label="หมายเหตุ (TH)"
                    error={Boolean(errors.remarkTh)}
                    helperText={errors.remarkTh?.message}
                    multiline
                    rows={2}
                    rowsMax={16}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="remarkEn"
                control={control}
                defaultValue={defaultFormData.remarkEn ?? ""}
                render={({ field }) => (
                  <TextField
                    variant="outlined"
                    fullWidth
                    {...field}
                    label="หมายเหตุ (EN)"
                    error={Boolean(errors.remarkEn)}
                    helperText={errors.remarkEn?.message}
                    multiline
                    rows={2}
                    rowsMax={16}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="fileId"
                control={control}
                defaultValue={defaultFormData.fileId ?? ""}
                render={({ field: { value, onChange } }) => (
                  <ImageUpload
                    fileId={value}
                    label={"รูปใบรับประกัน"}
                    onChange={onChange}
                  />
                )}
              />
            </Grid>

            <Grid item xs={6}>
              <Box mt={1}>
                <FormControl component="fieldset">
                  <FormLabel>รูปแบบการรับประกัน</FormLabel>
                  <Box ml={1} mt={1}>
                    <Controller
                      name="type"
                      control={control}
                      render={({ field }) => (
                        <RadioGroup aria-label="type" row {...field}>
                          {warrantyTypes.map(wt => (
                            <FormControlLabel
                              key={wt.value}
                              value={wt.value}
                              control={<Radio color="primary" />}
                              label={wt.label}
                            />
                          ))}
                        </RadioGroup>
                      )}
                    />
                  </Box>
                </FormControl>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box mt={1}>
                <ShowValue
                  label="วันที่ซื้อ"
                  value={
                    userProduct.receipt?.purchasedDate &&
                    formatDate(userProduct.receipt.purchasedDate)
                  }
                />
                <ShowValue
                  label="รูปใบเสร็จ"
                  value={
                    userProduct.receipt?.imageId && (
                      <ImageAvatar
                        renderAsLink
                        fileId={userProduct.receipt?.imageId}
                      />
                    )
                  }
                />
              </Box>
            </Grid>

            {type === "DURATION" && (
              <>
                <Grid item xs={4} md={2}>
                  <Controller
                    name="years"
                    control={control}
                    defaultValue={defaultFormData.years ?? 0}
                    render={({ field }) => (
                      <TextField
                        type="number"
                        variant="outlined"
                        fullWidth
                        inputProps={{
                          min: 0,
                          step: 1
                        }}
                        {...field}
                        label="ปี"
                        error={Boolean(errors.years)}
                        helperText={errors.years?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={4} md={2}>
                  <Controller
                    name="months"
                    control={control}
                    defaultValue={defaultFormData.months ?? 0}
                    render={({ field }) => (
                      <TextField
                        type="number"
                        variant="outlined"
                        fullWidth
                        inputProps={{
                          min: 0,
                          step: 1
                        }}
                        {...field}
                        label="เดือน"
                        error={Boolean(errors.months)}
                        helperText={errors.months?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={4} md={2}>
                  <Controller
                    name="days"
                    control={control}
                    defaultValue={defaultFormData.days ?? 0}
                    render={({ field }) => (
                      <TextField
                        type="number"
                        variant="outlined"
                        fullWidth
                        inputProps={{
                          min: 0,
                          step: 1
                        }}
                        {...field}
                        label="วัน"
                        error={Boolean(errors.days)}
                        helperText={errors.days?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <Controller
                    name="startDate"
                    control={control}
                    defaultValue={defaultFormData.startDate ?? ""}
                    render={({ field }) => (
                      <TextField
                        type="date"
                        variant="outlined"
                        fullWidth
                        {...field}
                        label="วันที่เริ่มรับประกัน"
                        InputLabelProps={{ shrink: true }}
                        error={Boolean(errors.startDate)}
                        helperText={errors.startDate?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <Controller
                    name="endDate"
                    control={control}
                    defaultValue={defaultFormData.endDate ?? ""}
                    render={({ field }) => (
                      <TextField
                        type="text"
                        variant="outlined"
                        fullWidth
                        disabled
                        {...field}
                        label="วันที่สิ้นสุดรับประกัน"
                        placeholder="ระบบคำนวณให้"
                        InputLabelProps={{ shrink: true }}
                        error={Boolean(errors.endDate)}
                        helperText={errors.endDate?.message}
                      />
                    )}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Divider light />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Controller
                    name="active"
                    control={control}
                    defaultValue={defaultFormData.active}
                    render={({ field: { value, onChange } }) => (
                      <Box ml={1}>
                        <Checkbox
                          checked={value}
                          onChange={onChange}
                          color="primary"
                        />
                      </Box>
                    )}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="primary">
            Cancel
          </Button>
          {!defaultValue ? (
            <Button
              key="addBtn"
              variant="contained"
              color="primary"
              type="submit"
            >
              Add
            </Button>
          ) : (
            <Button
              key="updateBtn"
              variant="contained"
              color="primary"
              type="submit"
            >
              Update
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UserProductWarrantyModal;
